import type { HomePageData } from "../services/HomeService";

export type HomePrimaryState = "onboarding" | "active" | "no_meal_today";
export type HomeSecondaryState = "closed_loop" | "idle";

export interface DerivedHomeAppState {
  primary: HomePrimaryState;
  secondary: HomeSecondaryState;
}

const isOnboardingIncomplete = (): boolean => {
  const complete = localStorage.getItem("glucera_onboarding_complete");
  return complete !== "true";
};

/** True when the user has logged at least one meal today (homepage / meal page). */
export const hasMealsLoggedToday = (
  homeData: HomePageData | undefined
): boolean => {
  const mealsLogged =
    homeData?.["Food GHI"]?.meals_logged ??
    homeData?.["Bite Balance Score"]?.meals_logged ??
    0;
  const calories = homeData?.["Today's Nutrition"]?.["Total Calories"] ?? 0;
  return mealsLogged > 0 || calories > 0;
};

export const deriveHomeAppState = (
  homeData: HomePageData | undefined,
  options: {
    selectedDate: string;
    todayDate: string;
    hasCgmReading?: boolean;
    /** Pass homeData?.["User Status"]?.is_new_user — API takes precedence over localStorage */
    isNewUser?: boolean;
  }
): DerivedHomeAppState => {
  // API is source of truth; fall back to localStorage when API data not yet available
  const isNew = options.isNewUser !== undefined
    ? options.isNewUser
    : isOnboardingIncomplete();

  if (isNew) {
    return { primary: "onboarding", secondary: "idle" };
  }

  const mealsLogged =
    homeData?.["Food GHI"]?.meals_logged ??
    homeData?.["Bite Balance Score"]?.meals_logged ??
    0;

  const isToday = options.selectedDate === options.todayDate;
  const primary: HomePrimaryState =
    isToday && mealsLogged === 0 ? "no_meal_today" : "active";

  const secondary: HomeSecondaryState = options.hasCgmReading
    ? "closed_loop"
    : "idle";

  return { primary, secondary };
};
